import React, { useState, useRef, useEffect } from "react";
import { Upload, Loader2, FileText, X, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { publicApi } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { useToast } from "../hooks/use-toast";
import MarketingConsent from "./MarketingConsent";

const MAX_MB = 10;
const ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export default function CvUploadModal({ open, onClose, vacancy = null }) {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const startedAt = useRef(Date.now());
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", age: "", role: "", message: "", website: "" });
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      startedAt.current = Date.now();
      if (vacancy?.title) setForm((f) => ({ ...f, role: vacancy.title }));
    }
  }, [open, vacancy]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setForm({ name: "", email: "", phone: "", address: "", age: "", role: "", message: "", website: "" });
    setFile(null); setConsent(false); setSubmitting(false); setDone(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const handleClose = () => { reset(); onClose(); };

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast({ title: "File too large", description: `Please keep your CV under ${MAX_MB}MB.`, variant: "destructive" });
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast({ title: "CV required", description: "Please attach your CV (PDF or Word).", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (vacancy?.id) fd.append("vacancy_id", vacancy.id);
      fd.append("marketing_consent", consent ? "true" : "false");
      fd.append("elapsed_ms", String(Date.now() - startedAt.current));
      fd.append("cv", file, file.name);
      await publicApi.applyCareers(fd);
      trackEvent("cv_application", { role: form.role || "general" });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.status === 429
        ? "Too many submissions. Please try again shortly."
        : (err?.response?.data?.detail || "Something went wrong. Please try again.");
      toast({ title: "Couldn't send your CV", description: msg, variant: "destructive" });
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cv-upload-modal">
        {done ? (
          <div className="text-center py-6" data-testid="cv-upload-success">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 text-green-600 mb-5"><CheckCircle2 size={34} /></span>
            <DialogTitle className="font-display text-navy text-2xl">Thanks — CV received!</DialogTitle>
            <DialogDescription className="text-slate-500 mt-3 leading-relaxed">
              We've got your CV safely and sent you a confirmation email. A member of the team will be in touch if there's a potential fit.
            </DialogDescription>
            <Button onClick={handleClose} data-testid="cv-upload-close" className="mt-6 bg-navy hover:bg-navy-deep text-white font-bold rounded-full px-8 py-6">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-navy text-2xl">{vacancy?.title ? `Apply — ${vacancy.title}` : "Send us your CV"}</DialogTitle>
              <DialogDescription className="text-slate-500">
                {vacancy?.title
                  ? "Tell us a little about yourself and upload your CV. We'll be in touch if there's a fit."
                  : "Tell us a little about yourself and upload your CV. We'll be in touch if there's a fit."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="cv-upload-form">
              {/* Honeypot */}
              <input type="text" value={form.website} onChange={set("website")} name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden />

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cv-name" className="text-navy font-semibold">Full name *</Label>
                  <Input id="cv-name" data-testid="cv-name" required value={form.name} onChange={set("name")} placeholder="Jane Smith" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="cv-email" className="text-navy font-semibold">Email *</Label>
                  <Input id="cv-email" data-testid="cv-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@email.co.uk" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="cv-phone" className="text-navy font-semibold">Phone</Label>
                  <Input id="cv-phone" data-testid="cv-phone" value={form.phone} onChange={set("phone")} placeholder="07123 456789" className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="cv-age" className="text-navy font-semibold">Age</Label>
                  <Input id="cv-age" data-testid="cv-age" inputMode="numeric" value={form.age} onChange={set("age")} placeholder="e.g. 28" className="mt-1.5" />
                </div>
              </div>

              <div>
                <Label htmlFor="cv-address" className="text-navy font-semibold">Address</Label>
                <Input id="cv-address" data-testid="cv-address" value={form.address} onChange={set("address")} placeholder="Town / postcode" className="mt-1.5" />
              </div>

              <div>
                <Label htmlFor="cv-role" className="text-navy font-semibold">Role you're interested in</Label>
                <Input id="cv-role" data-testid="cv-role" value={form.role} onChange={set("role")} placeholder="e.g. Bookkeeper, Client Manager, or open to anything" className="mt-1.5" />
              </div>

              <div>
                <Label htmlFor="cv-message" className="text-navy font-semibold">A little about you</Label>
                <Textarea id="cv-message" data-testid="cv-message" value={form.message} onChange={set("message")} placeholder="What are you looking for and what makes you a great fit?" rows={3} className="mt-1.5" />
              </div>

              {/* File upload */}
              <div>
                <Label className="text-navy font-semibold">Your CV * <span className="font-normal text-slate-400">(PDF or Word, max {MAX_MB}MB)</span></Label>
                {!file ? (
                  <button
                    type="button"
                    data-testid="cv-file-trigger"
                    onClick={() => fileRef.current?.click()}
                    className="mt-1.5 w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 hover:border-orange rounded-xl py-7 text-slate-500 hover:text-orange transition-colors"
                  >
                    <Upload size={22} />
                    <span className="text-sm font-semibold">Click to upload your CV</span>
                  </button>
                ) : (
                  <div className="mt-1.5 flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid="cv-file-chosen">
                    <span className="w-9 h-9 rounded-lg bg-orange/10 text-orange flex items-center justify-center shrink-0"><FileText size={18} /></span>
                    <span className="text-sm text-navy font-semibold truncate flex-1">{file.name}</span>
                    <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} data-testid="cv-file-remove" className="w-8 h-8 rounded-lg bg-white hover:bg-red-50 text-slate-400 hover:text-red-500 flex items-center justify-center shrink-0"><X size={16} /></button>
                  </div>
                )}
                <input ref={fileRef} type="file" accept={ACCEPT} data-testid="cv-file-input" onChange={(e) => pickFile(e.target.files?.[0])} className="hidden" />
              </div>

              <MarketingConsent checked={consent} onChange={setConsent} testId="cv-marketing-consent" />

              <Button type="submit" disabled={submitting} data-testid="cv-upload-submit" className="w-full bg-orange hover:bg-orange-dark text-white font-bold rounded-full py-6">
                {submitting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Sending...</> : <>Send my CV <Upload size={16} className="ml-2" /></>}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
